import { useMemo, type JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import { fmtDuration, fmtSimTime } from '../shell/format';
import { PRI_COLOUR } from '../alarms/AlarmBanner';
import { bestFor, recordBest } from './bests';

function Stars({ n }: { n: number }): JSX.Element {
  return (
    <span style={{ letterSpacing: 3, fontSize: 22 }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{ color: i < n ? 'var(--pri-medium)' : 'var(--line-dim)' }}>
          ★
        </span>
      ))}
    </span>
  );
}

/** End-of-shift report: score, KPIs, a timeline of what happened, and one tip. */
export function ShiftReport({ onClose }: { onClose(): void }): JSX.Element | null {
  const snap = useEngine((s) => s.snap);
  const events = useEngine((s) => s.events);
  const plant = useEngine((s) => s.plant);

  const timeline = useMemo(
    () =>
      events
        .filter((e) => e.k === 'trip' || e.k === 'objective' || e.k === 'scenario' || e.k === 'bypass' || e.k === 'reset')
        .slice(-40),
    [events],
  );

  const seed = useEngine((s) => s.seed);
  const replay = useEngine((s) => s.replay);
  const replaying = useEngine((s) => s.replaying);

  const challenge = useMemo(() => {
    if (!plant || !snap?.scenario) return null;
    const cfg = plant.scenarios.find((s) => s.id === snap.scenario!.id);
    if (!cfg?.challengeLoop) return null;
    const loop = snap.kpi.loops.find((l) => l.id === cfg.challengeLoop);
    return { cfg, iae: loop?.iae ?? 0 };
  }, [plant, snap]);

  const best = useMemo(() => {
    if (!plant || !snap?.scenario || !snap.score) return null;
    const prev = bestFor(plant.id, snap.scenario.id);
    recordBest(
      plant.id,
      snap.scenario.id,
      { score: snap.score.total, stars: snap.stars, seed, iae: challenge?.iae },
      challenge !== null,
    );
    return prev;
  }, [plant, snap?.scenario?.id, snap?.score?.total, seed, challenge?.iae]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!snap?.score) return null;
  const { score, kpi, scenario, stars } = snap;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,14,0.82)', zIndex: 800, display: 'grid', placeItems: 'center', padding: 24 }}
      onClick={onClose}
    >
      <div
        className="scroll"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--panel)',
          border: '1px solid var(--line-dim)',
          borderRadius: 6,
          width: 'min(980px, 100%)',
          maxHeight: '100%',
          padding: 22,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 19 }}>Shift report</h2>
          <span className="muted">
            {plant?.name}
            {scenario ? ` · ${scenario.name}` : ' · free running'}
          </span>
          <span style={{ flex: 1 }} />
          <Stars n={stars} />
          <span className="num" style={{ fontSize: 26 }}>
            {score.total.toFixed(0)}
          </span>
        </div>
        <div className="muted num" style={{ fontSize: 11.5, marginBottom: 16 }}>
          {fmtDuration(kpi.elapsedS)} of shift · seed {snap.stepCount > 0 ? '' : ''}
          {kpi.operatorActions} operator actions · {kpi.throughput.toFixed(0)} {kpi.throughputUnits} produced
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10, marginBottom: 18 }}>
          {score.parts.map((p) => (
            <div key={p.id} style={{ background: 'var(--bg-deep)', border: '1px solid var(--line-dim)', borderRadius: 4, padding: '9px 11px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <strong style={{ fontSize: 12.5 }}>{p.label}</strong>
                <span className="muted num" style={{ fontSize: 10.5 }}>
                  weight {p.weight}
                </span>
                <span style={{ flex: 1 }} />
                <span className="num" style={{ fontSize: 14, color: p.score > 0.85 ? 'var(--ok)' : p.score > 0.6 ? 'var(--pri-medium)' : 'var(--pri-high)' }}>
                  {(p.score * 100).toFixed(0)}
                </span>
              </div>
              <div style={{ height: 4, background: 'var(--panel-2)', borderRadius: 2, margin: '6px 0 6px' }}>
                <div
                  style={{
                    width: `${p.score * 100}%`,
                    height: '100%',
                    borderRadius: 2,
                    background: p.score > 0.85 ? 'var(--ok)' : p.score > 0.6 ? 'var(--pri-medium)' : 'var(--pri-high)',
                  }}
                />
              </div>
              <div className="muted" style={{ fontSize: 11, lineHeight: 1.4 }}>
                {p.detail}
              </div>
            </div>
          ))}
        </div>

        {scenario && (
          <section style={{ marginBottom: 18 }}>
            <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>Objectives</h3>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
              {scenario.objectives.map((o) => (
                <li key={o.id} style={{ color: o.failed ? 'var(--pri-high)' : o.met ? 'var(--ok)' : 'var(--text-mute)' }}>
                  {o.met && !o.failed ? '✓' : '✗'} {o.text}
                  {o.kind === 'maintain' && o.violationS > 0 && (
                    <span className="muted num" style={{ fontSize: 11 }}>
                      {' '}
                      — {o.violationS.toFixed(0)} s outside, {o.toleranceS} s allowed
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>What happened</h3>
          <div className="scroll" style={{ maxHeight: 190, background: 'var(--bg-deep)', borderRadius: 4, padding: '6px 10px' }}>
            {timeline.length === 0 && <div className="muted" style={{ fontSize: 12 }}>A quiet shift — nothing of note.</div>}
            {timeline.map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, fontSize: 12, padding: '2px 0' }}>
                <span className="num muted" style={{ width: 66, flexShrink: 0 }}>
                  {fmtSimTime(e.step * 0.1)}
                </span>
                <span
                  style={{
                    color:
                      e.k === 'trip'
                        ? PRI_COLOUR.emergency
                        : e.k === 'objective'
                          ? e.met
                            ? 'var(--ok)'
                            : 'var(--pri-high)'
                          : e.k === 'bypass'
                            ? 'var(--pri-medium)'
                            : 'var(--text-dim)',
                  }}
                >
                  {e.k === 'trip'
                    ? `${e.text}${e.firstOut ? ` — first out: ${e.firstOut}` : ''}`
                    : e.k === 'objective'
                      ? `${e.met ? 'Met' : 'Failed'}: ${e.text}`
                      : e.k === 'bypass'
                        ? `${e.on ? 'Bypassed' : 'Bypass removed'} ${e.id} — ${e.reason}`
                        : e.k === 'reset'
                          ? `Reset ${e.text}`
                          : e.text}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section
          style={{
            background: 'var(--bg-deep)',
            borderLeft: '3px solid var(--accent)',
            borderRadius: 4,
            padding: '11px 14px',
            marginBottom: 18,
          }}
        >
          <div className="muted" style={{ fontSize: 11, marginBottom: 4, letterSpacing: 1 }}>
            ONE THING FOR NEXT TIME
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.55 }}>{score.tip}</div>
        </section>

        {challenge && (
          <section
            style={{ background: 'var(--bg-deep)', borderRadius: 4, padding: '11px 14px', marginBottom: 18 }}
          >
            <div className="muted" style={{ fontSize: 11, letterSpacing: 1, marginBottom: 4 }}>
              TUNING CHALLENGE — {challenge.cfg.challengeLoop}
            </div>
            <div style={{ display: 'flex', gap: 24, alignItems: 'baseline' }}>
              <div>
                <span className="num" style={{ fontSize: 22 }}>
                  {challenge.iae.toFixed(0)}
                </span>{' '}
                <span className="muted">%·s integrated absolute error</span>
              </div>
              <div className="muted num" style={{ fontSize: 12 }}>
                target ≤ {challenge.cfg.challengeTarget}
              </div>
              {best?.iae !== undefined && (
                <div className="muted num" style={{ fontSize: 12 }}>
                  your best {best.iae.toFixed(0)} (seed {best.seed})
                </div>
              )}
            </div>
          </section>
        )}

        {best && !challenge && (
          <div className="muted num" style={{ fontSize: 11.5, marginBottom: 12 }}>
            Previous best on this shift: {best.score.toFixed(0)} ({best.stars}★, seed {best.seed})
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            disabled={replaying}
            title="Rebuild the whole shift from the seed and your action log, then watch it back"
            onClick={() => void replay()}
          >
            {replaying ? 'Replaying…' : 'Replay this shift'}
          </button>
          <button onClick={onClose}>Stay on the plant</button>
        </div>
      </div>
    </div>
  );
}
